import { CSSProperties, ReactNode } from 'react';

import { StyleProps } from '@chakra-ui/react';

import { WoolfProgram } from '~/features/accreditation/components/woolf-program-card';
import { FormalDegreeKey } from '~/features/checkout/constants';

export type AccreditationColorScheme = 'blue' | 'crimson';

export const accreditationColorHex: Record<AccreditationColorScheme, Record<string, string>> = {
  blue: {
    500: '#175CFF',
    700: '#00298C',
  },
  crimson: {
    500: '#9C0D22',
    700: '#540916',
  },
};

export const accreditationGradients: Record<
  AccreditationColorScheme,
  {
    statsBg: string;
    statsAccentStart: string;
    prerequisitesBg: string;
    prerequisitesBorder: string;
    resourcesCardBg: string;
    topBarBg: { base: string; lg: string };
  }
> = {
  blue: {
    statsBg: 'linear(to-tr, #031643 0%, #00298C 50%, #007D9F 100%)',
    statsAccentStart: '#89E6FF',
    prerequisitesBg: 'linear(to-br, #00298C, #000B26)',
    prerequisitesBorder: 'linear(to-br, rgba(0,41,140,.64), #00298C)',
    resourcesCardBg: 'linear(135deg, rgba(8, 55, 169, 1) 0%, rgba(3, 22, 67, 1) 50%)',
    topBarBg: {
      base: 'linear(to-b, #071C4E, #060A12)',
      lg: 'linear(to-b, #060A12, #071C4E)',
    },
  },
  crimson: {
    statsBg: 'linear(to-tr, #2B050C 0%, #540916 50%, #9C0D22 100%)',
    statsAccentStart: '#F4737F',
    prerequisitesBg: 'linear(to-br, #540916, #1A0308)',
    prerequisitesBorder: 'linear(to-br, rgba(84,9,22,.64), #540916)',
    resourcesCardBg: 'linear(135deg, rgba(156, 13, 34, 1) 0%, rgba(43, 5, 12, 1) 50%)',
    topBarBg: {
      base: 'linear(to-b, #540916, #0A0B0F)',
      lg: 'linear(to-b, #0A0B0F, #540916)',
    },
  },
};

export interface AccreditationHeroProps {
  title: string;
  description: string;
  brochureUri: string;
  backgroundImages: {
    mobile: string;
    tablet?: string;
    desktop: string;
    objectPosition?: {
      mobile?: CSSProperties['objectPosition'];
      tablet?: CSSProperties['objectPosition'];
      desktop?: CSSProperties['objectPosition'];
    };
  };
  backgroundImageOverlayGradient?: StyleProps['bgGradient'] | null;
  heroTextMaxWidth?: StyleProps['maxWidth'];
}

export interface AccreditationTopBarItem {
  id: string;
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
}

export interface AccreditationStatItem {
  number: string;
  suffixChar?: string;
  descriptionTop: string;
  descriptionBottom: string;
  suffix: number;
}

export interface AccreditationStatSource {
  suffix: number;
  label: string;
  href: string;
}

export interface AccreditationStatsProps {
  heading: string;
  subheading: string;
  stats: AccreditationStatItem[];
  sources: AccreditationStatSource[];
}

export interface AccreditationPathStep {
  icon: ReactNode;
  title: string;
  description: string;
}

export interface AccreditationAboutProps {
  description: ReactNode;
  certificateImage: string;
}

export interface AccreditationValuesProps {
  sectionTitle: string;
  sectionDescription: ReactNode;
  items: Array<{
    imageSrc: string;
    title: string;
    description: string;
  }>;
}

export interface AccreditationPrerequisitesProps {
  degreeTitle: string;
  feeDescription: string;
  prerequisites: string[];
  stampImage: string;
}

export interface AccreditationMilestonesProps {
  sectionTitle: string;
  electiveKeys: string[];
  electiveCreditHours: number;
  capstoneCreditHours?: number;
  coreDescription: string;
  capstoneDescription: string;
  electiveDescription: string;
  modalAlertText: string;
  corePrograms: WoolfProgram[];
  woolfToUdacityProgramKeys: Record<string, string[]>;
}

export interface AccreditationTestimonial {
  imagePath: string;
  labelKey: string;
  quoteKey: string;
  name: string;
  title: string;
  quoteColor: string;
  labelColor: string;
}

export interface AccreditationFaq {
  question: string;
  answer: string;
}

export interface AccreditationFooterProps {
  heading: string;
  description: string;
  backgroundImages: {
    mobile: string;
    desktop: string;
  };
}

export interface AccreditationFeaturedVideoCardProps {
  title: string;
  description?: string;
  portraitPath: string;
  href: string;
  speakerLabel?: string;
  background?: string;
}

export interface AccreditationResourcesProps {
  videos: Record<string, { id: string; title: string }>;
  featuredVideoCard?: AccreditationFeaturedVideoCardProps;
  /** First resources card (short Q&A video): tag + headline — owned per degree in content configs */
  videoQaCard: {
    heading: string;
    tag: string;
  };
}

export interface AccreditationNewsItem {
  type: 'podcast' | 'article';
  tag: string;
  title: string;
  description: string;
  href: string;
}

export interface AccreditationPageContentProps {
  colorScheme?: AccreditationColorScheme;
  formalDegreeKey: FormalDegreeKey;
  hero: AccreditationHeroProps;
  topBarItems: AccreditationTopBarItem[];
  values: AccreditationValuesProps;
  stats: AccreditationStatsProps;
  pathSteps: AccreditationPathStep[];
  pathTitle: string;
  pathDescription: string;
  about: AccreditationAboutProps;
  prerequisites: AccreditationPrerequisitesProps;
  milestones: AccreditationMilestonesProps;
  testimonials: AccreditationTestimonial[];
  resources?: AccreditationResourcesProps;
  faqs: AccreditationFaq[];
  faqsSupportLink: string;
  footer: AccreditationFooterProps;
  newsItems?: AccreditationNewsItem[];
}
